import { Box, Flex, HStack, Image, Text } from "@chakra-ui/react";
import { FeedCardHalo } from "./FeedCardHalo";
import { FeedBundleItem } from "./types";

interface FeedBundleCardProps {
  item: FeedBundleItem;
  isActive: boolean;
}

/**
 * Bundle promo card — image-forward layout with the comic covers dominating
 * the card height and the bundle name + owner anchored at the bottom over a
 * gradient. Tap → the bundle page on the creator's site (price/checkout lives
 * there).
 */
export function FeedBundleCard({ item, isActive }: FeedBundleCardProps) {
  // Halo color comes from the first cover so the surrounding glow matches
  // the dominant comic in the bundle.
  const haloSrc =
    item.comics[0]?.cover_url ?? item.comics[0]?.cover_small_url ?? null;
  return (
    <Box position="relative" w="100%" h="100%">
      <FeedCardHalo src={haloSrc} />
    <Box
      as="a"
      href={item.url}
      tabIndex={isActive ? 0 : -1}
      position="relative"
      display="block"
      w="100%"
      h="100%"
      zIndex={1}
      borderRadius="2xl"
      overflow="hidden"
      border="1px solid rgba(255,255,255,0.06)"
      color="white"
      cursor="pointer"
      _hover={{ borderColor: "rgba(255,255,255,0.18)" }}
      transition="border-color 120ms ease"
      bgGradient="linear(135deg, rgba(60, 36, 28, 0.95) 0%, rgba(20, 14, 14, 0.95) 100%)"
    >
      {/* Cover row fills the card; title strip sits over the bottom gradient. */}
      <HStack
        spacing={1}
        position="absolute"
        inset={0}
        align="stretch"
      >
        {item.comics.map((comic) => (
          <Box
            key={comic.comic_id}
            flex="1"
            minW="0"
            position="relative"
            bg="black"
          >
            {comic.cover_url ?? comic.cover_small_url ? (
              <Image
                src={comic.cover_url ?? comic.cover_small_url ?? ""}
                alt={comic.name}
                w="100%"
                h="100%"
                objectFit="cover"
              />
            ) : (
              <Box
                w="100%"
                h="100%"
                bgGradient="linear(135deg, orange.400 0%, red.700 100%)"
              />
            )}
          </Box>
        ))}
      </HStack>
      {/* Gradient scrim → keeps the bundle title legible over busy art. */}
      <Box
        position="absolute"
        left={0}
        right={0}
        bottom={0}
        h="55%"
        bgGradient="linear(to-t, rgba(0,0,0,0.95) 0%, rgba(0,0,0,0.55) 50%, transparent 100%)"
        pointerEvents="none"
      />
      {/* "BUNDLE" badge in the top-left so the user can identify the slot
          before they read anything else. */}
      <Flex
        position="absolute"
        top={3}
        left={3}
        align="center"
        px={2}
        py={1}
        borderRadius="md"
        bg="rgba(0,0,0,0.55)"
        backdropFilter="blur(6px)"
      >
        <Text
          fontSize="xs"
          fontWeight="extrabold"
          letterSpacing="0.16em"
          textTransform="uppercase"
          color="orange.300"
        >
          Bundle
        </Text>
      </Flex>
      {/* Bundle title + owner anchored at the bottom. */}
      <Flex
        position="absolute"
        left={0}
        right={0}
        bottom={0}
        direction="column"
        gap={0.5}
        px={4}
        pb={4}
        pt={6}
      >
        <Text
          fontSize="lg"
          fontWeight="bold"
          lineHeight="1.2"
          noOfLines={2}
          sx={{ textShadow: "0 2px 6px rgba(0,0,0,0.6)" }}
        >
          {item.name}
        </Text>
        <Text fontSize="xs" opacity={0.85} noOfLines={1}>
          by {item.artist_name}
        </Text>
      </Flex>
    </Box>
    </Box>
  );
}
